<template>

    <div class="strategy-brief-container row" v-if="strategyStatus === 'static'">

        <v-form class="strategy-form" v-model="isFormValid" @submit.prevent v-if="staticQuestion">
            <div class="label">{{ staticQuestion[0]?.question }}</div>
            <div class="static-qn-container">
                <div class="qn-header">
                    <div class="header-left">
                        <Svg height="22" :src="InfoIcon"></Svg>
                        <p class="header-txt">Expert Input Guide</p>
                    </div>
                    <button class="dropdown" @click="isOpen = !isOpen">
                        <Svg height="9" v-if="isOpen" width="12" :src="UpIcon"></Svg>
                        <Svg height="9" v-else width="12" :src="DownIcon"></Svg>
                    </button>
                </div>
                <div v-if="isOpen" class="qn-wrapper">
                    <p class="header-txt">How will you respond to these challenges?</p>
                    <p class="qn-txt">{{ questions }}</p>
                </div>
            </div>
            <v-textarea v-model="staticQuestion[0].answer" :rules="fieldRules" variant="outlined" density="compact"
                placeholder="[Start by describing the problem, your approach, and the impact you want to create…]"></v-textarea>
        </v-form>
    </div>

    <div class="strategy-brief-container output" v-show="strategyStatus === 'output'">
        <div class="output-header">
            <div class="output-header-left">Generated Strategy Output</div>

            <div v-if="!isEditingNarrative" class="output-header-right">
                <button class="edit-btn" @click="editStrategy">
                    <Svg :src="require('@/assets/icons/edit.svg')" fill="black"></Svg>
                </button>
            </div>
            <div v-else class="output-header-right">
                <button class="save-changes-btn" @click="saveStrategy" :disabled="previousVersion === editedVersion">
                    <Svg :src="require('@/assets/icons/save.svg')" fill="#262626"></Svg>
                    Save Changes
                </button>
                <button class="edit-btn" @click="cancelChanges">
                    <img :src="require('@/assets/icons/close.svg')"></img>
                </button>
            </div>
        </div>
        <div class="output-details">
            <div class="output-details-wrapper" v-html="previousVersion" ref="detailsContainer" :key="refresh"
                id="edit-container" v-if="!isEditingNarrative"></div>
            <div v-show="isEditingNarrative" class="editor-wrapper">
                <div ref="editorContainer">
                    <div ref="editor" class="quill-editor"></div>
                </div>
            </div>
        </div>
    </div>
    <CommonPopup v-if="showCommonPopup" :showCommonPopup="showCommonPopup" :popupType="popupType"
        @confirm-action="handlePopupConfirmation" />
</template>

<script>
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import axiosInstance from '@/axiosInterceptor';
import { Eventbus } from '@/plugins/eventBus';
import CommonPopup from '@/components/CommonPopup.vue';
import InfoIcon from '@/assets/icons/clr-info-icon.svg';
import UpIcon from '@/assets/icons/up-icon.svg';
import DownIcon from '@/assets/icons/down-icon.svg';
import { marked } from 'marked';
export default {
    props: {
        modelValue: {
            type: Object
        }
    },
    components: {
        CommonPopup,
    },
    data() {
        return {
            isFormValid: false,
            fieldRules: [
                (v) => !!v || 'This field is required'
            ],
            selectedQues: [],
            quesLoader: false,
            regenerateLoader: [],
            staticQuestion: [],
            strategyStatus: null,
            isEditingNarrative: false,
            previousVersion: null,
            editedVersion: null,
            editor: null,
            refresh: 0,
            showCommonPopup: false,
            popupType: null,
            isRegeneratingQues: false,
            isOpen: true,
            InfoIcon, UpIcon, DownIcon,
            questions: "What's your specific plan to address these concerns? What makes you uniquely positioned to solve this? Name 2-3 strategies that will create the biggest impact and provide proof where possible."
        }
    },
    computed: {
        strategy: {
            get() {
                this.regenerateLoader = Array(this.modelValue.length).fill(false);
                return this.modelValue;
            }
        }
    },
    watch: {
        staticQuestion: {
            deep: true,
            handler(newValue) {
                this.$emit('update:modelValue', {
                    ...this.modelValue,
                    staticQues: newValue
                });
            }
        },
        isRegeneratingQues() {
            this.$emit('update:isRegeneratingQues', this.isRegeneratingQues);
        },
        editedVersion() {
            this.$emit('update:modelValue', {
                ...this.modelValue,
                generatedContent: this.editedVersion
            });
        },
        isFormValid() {
            this.$emit('update:isFormValid', this.isFormValid);
        },
        isEditingNarrative() {
            this.$emit('update:isEditingNarrative', this.isEditingNarrative);
        }
    },
    methods: {
        handlePopupConfirmation(val) {
            this.showCommonPopup = false;
            if (val === 'yes') {
                if (this.popupType === 'withoutSave') {
                    this.isEditingNarrative = false;
                }
            }
        },
        getFieldRules(isRequired) {
            return isRequired
                ? [(v) => !!v || 'This field is required']
                : [];
        },
        editStrategy() {
            this.isEditingNarrative = !this.isEditingNarrative;
            this.editedVersion = this.previousVersion;
            this.editor.root.innerHTML = this.previousVersion;
        },
        saveStrategy() {
            let payload = {
                narrativeId: this.$route.params.narrativeId,
                strategy: {
                    strategyContent: this.editedVersion
                },
                lastUpdatedStage: 'Strategy'
            };
            axiosInstance.put('/narrative/saveDraft', payload)
                .then(() => {
                    this.previousVersion = this.editedVersion;
                    Eventbus.emit('commonSnackbar', {
                        snackType: 'save',
                        message: "Changes saved successfully"
                    })
                })
                .catch((error) => {
                    console.error(error);
                    Eventbus.emit('commonSnackbar', {
                        snackType: 'error',
                        message: "Failed to save changes"
                    })
                })
                .finally(() => {
                    this.isEditingNarrative = !this.isEditingNarrative;
                });
        },
        cancelChanges() {
            if (this.previousVersion === this.editedVersion) {
                this.isEditingNarrative = !this.isEditingNarrative;
            } else {
                this.popupType = 'withoutSave';
                this.showCommonPopup = true;
            }
        },
        
    },
    mounted() {
        if (Object.keys(this.strategy).length > 0) {
            if (this.strategy.generatedContent) {
                this.strategyStatus = 'output';
                this.previousVersion = this.strategy.generatedContent;
            } else {
                this.strategyStatus = 'static';
                this.staticQuestion = this.strategy?.staticQues;
            }

        } else {
            this.strategyStatus = null;
        }

        this.refresh += 1;

        if (this.strategyStatus === 'output') {
            if (this.$refs.editor) {
                this.editor = new Quill(this.$refs.editor, {
                    theme: "snow",
                });
                this.editor.on("text-change", () => {
                    this.editedVersion = this.editor.root.innerHTML;
                });
                this.refresh += 1;
            } else {
                console.error("Quill container not found.");
            }
        }
    }
}
</script>

<style scoped>
.strategy-brief-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 1em;
}

.row {
    align-items: center;
    padding: 2rem 4rem;
}

.output {
    width: 48%;
    padding: 1rem 0rem;
}

.static-qn-container {
    display: flex;
    flex-direction: column;
    font-weight: 600;
    color: var(--dark-grey-color);
    border-radius: 8px;
    height: fit-content;
    overflow: hidden;
    border: 1px solid #E5E5E5;
}

.qn-header {
    display: flex;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: #EEEEEE;
    align-items: center;
    justify-content: space-between;
}

.header-txt {
    display: flex;
    align-items: center;
}

.header-left {
    display: flex;
    flex-direction: row;
    gap: 0.75rem;
}

.qn-wrapper {
    display: flex;
    flex-direction: column;
    padding: 1rem 1.25rem;
    gap: 0.5rem;
    background-color: #F5F5F5;
    border-top: 1px solid #E5E5E5;
}

.qn-txt {
    color: #737373;
    font-weight: 400;
    font-size: 14px;
}

.dropdown {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 1.8rem;
    height: 1.8rem;
    background: #FFFFFF;
    border: 1px solid #d4d4d4;
    border-radius: 0.5rem;
}

.strategy-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem 2rem;
    border-radius: 12px;
    background: #FAFAFA;
    width: 75%;
    flex: 1;
}

.label {
    font-size: 16px;
    font-weight: 500;
    text-align: left;
    color: #171717;
}

.label-bold {
    font-weight: 600;
}

.label-italic {
    font-style: italic;
}


.header-title {
    font-size: 24px;
    font-weight: 600;
    text-align: left;
    color: #FAFAFA;
}

.header-name {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.header-subtitle {
    font-size: 14px;
    font-weight: 400;
    text-align: center;
    color: #E5E5E5;
}

.body {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.ques-ans-container {
    display: flex;
    gap: 16px;
    align-items: baseline;
}

.select-box {
    appearance: none;
    width: 20px;
    height: 20px;
    border: 2px solid white;
    border-radius: 2px;
    background-color: transparent;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
}

.select-box:checked {
    background-color: #DF009E;
    border-color: #DF009E;
}

.select-box:checked::after {
    content: '';
    background-image: url('@/assets/icons/tick-icon.svg');
    background-size: cover;
    background-repeat: no-repeat;
    display: block;
    width: 12px;
    height: 12px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.ques-ans-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    border: 1px solid #D4D4D4;
    background: #FFFFFF;
    font-size: 16px;
    font-weight: 500;
    text-align: left;
    border-radius: 8px;
}

.ques-ans {
    display: flex;
    flex-direction: column;
    gap: 1em;
    padding: 1.2em 2em;
}

.ques-btn-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.ques {
    color: #171717;
    font-size: 16px;
    font-weight: 500;
}

.ans-wrapper {
    border: 1px solid #D4D4D4;
    background: #F5F5F5;
    border-radius: 4px;
    padding: 20px;
    color: #262626;
    font-size: 16px;
    font-weight: 400;
}

.regenerate-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid #DF009E;
    color: #DF009E;
    border-radius: 8px;
}

.output-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.output-header-left {
    font-size: 24px;
    font-weight: 600;
    text-align: left;
    color: #FAFAFA;
    font-family: 'san-serif', 'Inter';
}

.output-header-right {
    display: flex;
    gap: 1em;
}

.edit-btn {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border-radius: 50%;
    border: 1px solid #A3A3A3;
    background: white;
}

.save-changes-btn {
    width: 11rem;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    gap: 0.7rem;
    border-radius: 1.5rem;
    background: white;
    color: #262626;
    border: 1px solid #A3A3A3;
}

.output-details {
    display: flex;
    flex-direction: column;
    gap: 2em;
    padding: 3em;
    align-items: left;
    background-color: white;
    border-radius: 24px;
}

.output-details-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.output-details-title {
    font-size: 24px;
    font-weight: 600;
    text-align: left;
    color: #171717;
}
</style>

<style>
.output-details-wrapper ol {
    padding-left: 50px !important;
}
</style>